/// <reference types="@helsenorge/framework-utils/types/hn"/>

import { useState, useEffect, useContext, useRef } from 'react';

import classNames from 'classnames';

import { MenuType } from '../../types/entities';

import HeaderSkeleton from '@helsenorge/core-cms/header-skeleton';
import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { getUserLanguage, useSetLanguageEvent } from '@helsenorge/framework-utils/hn-language';
import { getAssetsUrl } from '@helsenorge/framework-utils/hn-page';
import { erTjenester } from '@helsenorge/framework-utils/hn-proxy-service';
import { HeaderFooterEvents } from '@helsenorge/framework-utils/web-component/constants';
import WebCompConsumer from '@helsenorge/framework-utils/web-component/consumer';
import { HNeventSetUserLoading, type VisPersonvelgerDetail } from '@helsenorge/framework-utils/web-component/events';
import type { WebCompProps } from '@helsenorge/framework-utils/web-component/register';
import useWebComponentEvents from '@helsenorge/framework-utils/web-component/useWebComponentEvents';
import WithStore from '@helsenorge/framework-utils/web-component/with-store';

import { globalStateContext, globalReducer } from '../../store';
import { getUser, erInnloggetOgErTjenester, getHeaderFooter, type DispatchAction, getInnloggetData } from '../../store/actions';
import { setLanguage } from '../../store/actions/language';
import { setVisPersonvelger } from '../../store/actions/personvelger';
import { type GlobalState, initialState } from '../../store/initialState';
import { reducer } from '../../store/reducers';
import { isReferrer } from '../../utils/referrer';
import AppInvitationWrapper from '../app-invitation';
import Header from '../header';
import SkipToContent from '../skip-to-content';

import styles from './styles.module.scss';

export interface HeaderWebCompProps extends WebCompProps {
  simplified?: string | boolean;
  anonymous?: string | boolean;
  hide?: string | boolean;
  language?: LanguageLocales;
}

interface State {
  isMenuActive: boolean;
  hasVarsel: boolean;
  menuType?: MenuType;
  prevMenuType?: MenuType;
  simplifiedHeader?: boolean;
  firstTimeLogin?: boolean;
  anonymousHeader: boolean;
  hiddenHeader?: boolean;
}

export const HeaderWrapper: React.FC<HeaderWebCompProps> = props => {
  const { simplified, anonymous, hide } = props;
  const ref = useRef<HTMLDivElement>(null);
  const { subscribe } = useWebComponentEvents<HTMLDivElement>('hn-webcomp-header-footer', ref);
  const globalState = useContext(globalStateContext);
  const globalDispatch = useContext(globalReducer);

  const [state, setState] = useState<State>({
    hasVarsel: false,
    menuType: undefined,
    isMenuActive: false,
    prevMenuType: undefined,
    simplifiedHeader: isReferrer('oidc') || (simplified !== undefined && simplified !== 'false'),
    firstTimeLogin: false,
    anonymousHeader: anonymous !== undefined && anonymous !== 'false',
    hiddenHeader: hide !== undefined && hide !== 'false',
  });

  const language = props.language === LanguageLocales.NORWEGIAN.toLowerCase() ? LanguageLocales.NORWEGIAN : props.language;

  // Effect run on init
  useEffect(() => {
    if (window && window.HN) {
      const userLanguage = getUserLanguage() || language || LanguageLocales.NORWEGIAN; // @todo Burde language fra prop prioriteres før getUserLanguage()
      getHeaderFooter(globalDispatch, 'Det oppstod en feil ved henting av menyen.', userLanguage);
      setLanguage(globalDispatch, userLanguage);
      erInnloggetOgErTjenester(globalDispatch);

      return subscribe('hn-webcomp-header-footer-event-setsimplifiedheader', (event: CustomEvent) => {
        const simplifiedHeader = event.detail.simplifiedHeader;
        setState(state => ({ ...state, simplifiedHeader }));
      });
    }
  }, []);

  useEffect(() => {
    if (window && window.HN) {
      return subscribe(HeaderFooterEvents.setfirsttimelogin, (event: CustomEvent) => {
        const firstTimeLogin = event.detail.firstTimeLogin;
        setState({ ...state, firstTimeLogin });
      });
    }
  }, []);

  useEffect(() => {
    if (window && window.HN) {
      return subscribe(HeaderFooterEvents.setanonymousheader, (event: CustomEvent) => {
        const anonymousHeader = event.detail.anonymousHeader;
        setState(state => ({ ...state, anonymousHeader }));
      });
    }
  }, []);

  useEffect(() => {
    if (window && window.HN) {
      return subscribe(HeaderFooterEvents.sethiddenheader, (event: CustomEvent) => {
        const hiddenHeader = event.detail.hiddenHeader;
        setState(state => ({ ...state, hiddenHeader }));
      });
    }
  }, []);

  const handleVisPersonvelgerUpdate = (event: CustomEvent<VisPersonvelgerDetail>): void => {
    setVisPersonvelger(globalDispatch, event.detail.visPersonvelger);
  };

  useEffect(() => {
    window.addEventListener(HeaderFooterEvents.setvispersonvelger, handleVisPersonvelgerUpdate);

    return (): void => {
      window.removeEventListener(HeaderFooterEvents.setvispersonvelger, handleVisPersonvelgerUpdate);
    };
  }, []);

  useSetLanguageEvent(newLanguage => {
    if (erTjenester()) {
      getHeaderFooter(globalDispatch, 'Det oppstod en feil ved henting av menyen.', newLanguage);
      setLanguage(globalDispatch, newLanguage);
    }
  });

  // Effect for getting user data and meldinger
  useEffect(() => {
    if (globalState.isAuthorized && globalState.headerFooter) {
      if (globalState.isMinHelseTjenester) {
        getUser(globalDispatch, globalState.headerFooter.header.personvelger?.errorHentBruker ?? 'Feil under henting av bruker');
        HNeventSetUserLoading(true);
      } else {
        getInnloggetData(
          globalDispatch,
          globalState.headerFooter.header.personvelger?.errorInnloggetBruker ?? 'Feil under henting av data om innlogging'
        );
      }
    }
  }, [globalState.isAuthorized, globalState.headerFooter]);

  useEffect(() => {
    if (globalState.bruker) {
      HNeventSetUserLoading(false);
    }
  }, [globalState.bruker]);

  if (!globalState.headerFooter) {
    return <HeaderSkeleton />;
  }

  const setMenuType = (type: MenuType): void => {
    setState(prevState => ({ ...prevState, isMenuActive: true, menuType: type, prevMenuType: prevState.menuType }));
  };

  const closeMenu = (): void => {
    setState(prevState => ({ ...prevState, isMenuActive: false, menuType: undefined }));
  };

  const isBlueberry = globalState.isAuthorized && state.isMenuActive && state.menuType === MenuType.profile;
  const isCherry = state.isMenuActive && (state.menuType === MenuType.basic || state.menuType === MenuType.search);

  const getMenuColor = (): string => {
    if (isBlueberry) {
      return 'blueberry';
    }
    if (isCherry) {
      return 'cherry';
    }
    return 'unsetcolor';
  };

  const headerWrapperStyles = classNames(styles['header-wrapper'], {
    [styles['header-wrapper--active']]: state.isMenuActive,
    [styles['header-wrapper--blueberry']]: isBlueberry,
    [styles['header-wrapper--cherry']]: isCherry,
    [styles['header-wrapper--unsetcolor']]: !isCherry && !isBlueberry,
    [styles['header-wrapper--has-profile-button']]:
      !state.simplifiedHeader && !state.anonymousHeader && typeof globalState.bruker !== 'undefined',
  });
  const headerOverlayClasses = classNames(styles['header-overlay'], { [styles['header-overlay--active']]: state.isMenuActive });

  const renderHeader = (): React.ReactNode => (
    <div className={headerWrapperStyles}>
      <SkipToContent />
      <Header
        isActive={state.isMenuActive}
        isSimplified={state.simplifiedHeader}
        isFirstTimeLogin={state.firstTimeLogin}
        isAnonymous={state.anonymousHeader}
        menuType={state.menuType}
        setMenuType={setMenuType}
        closeMenu={closeMenu}
        menuColor={getMenuColor()}
      />
      <AppInvitationWrapper />

      <div className={headerOverlayClasses}></div>
    </div>
  );

  return (
    <>
      <div ref={ref}></div>

      {!state.hiddenHeader && renderHeader()}

      <WebCompConsumer
        domain={`${getAssetsUrl()}/personvelgeren`}
        entryName="src/index.tsx"
        componentName="hn-webcomp-session"
        templateName="hn-webcomp-personvelger-template"
        includeHelsenorgeCss
        componentProps={{
          isauthorized: globalState.isAuthorized ? 'true' : 'false',
          lightboxinactivitytitle: globalState.headerFooter.header.fasteTekster.lightboxInactivityTitle,
          lightboxinactivitydescription: globalState.headerFooter.header.fasteTekster.lightboxInactivityDescription,
          lightboxinactivitytimer: globalState.headerFooter.header.fasteTekster.lightboxInactivityTimer,
          lightboxinactivitydoyouwanttostay: globalState.headerFooter.header.fasteTekster.lightboxInactivityDoYouWantToStay,
          lightboxinactivitybuttonyes: globalState.headerFooter.header.fasteTekster.lightboxInactivityButtonYes,
          lightboxinactivitybuttonsignout: globalState.headerFooter.header.fasteTekster.lightboxInactivityButtonSignOut,
          inactivitysignedouttitle: globalState.headerFooter.header.fasteTekster.inactivitySignedOutTitle,
          inactivitysignedoutbuttongotoroot: globalState.headerFooter.header.fasteTekster.inactivitySignedOutButtonGoToRoot,
          inactivitysignedoutbuttonsigninagain: globalState.headerFooter.header.fasteTekster.inactivitySignedOutButtonSignInAgain,
          representasjonmodaltitle: globalState.headerFooter.header.fasteTekster.representasjonModalTitle,
          representasjonmodaldescription: globalState.headerFooter.header.fasteTekster.representasjonModalDescription,
          representasjonmodalknapp: globalState.headerFooter.header.fasteTekster.representasjonModalKnapp,
          representasjonmodalfeilmelding: globalState.headerFooter.header.fasteTekster.representasjonModalFeilmelding,
          representasjonmodalfeilmeldingdescription: globalState.headerFooter.header.fasteTekster.representasjonModalFeilmeldingDescription,
        }}
      />
    </>
  );
};
const props = {
  StateContext: globalStateContext,
  DispatchContext: globalReducer,
  reducer: reducer,
  initialState: initialState,
};

const ComponentWithStore: React.FC<HeaderWebCompProps> = optionalProps => {
  return (
    <WithStore<GlobalState, DispatchAction> {...props}>
      <HeaderWrapper {...optionalProps} />
    </WithStore>
  );
};

export default ComponentWithStore;
